import { useL } from "@/lib/useL";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, MessageCircle, Instagram, Facebook, Twitter, Youtube, Linkedin } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAITranslate } from "@/hooks/useAITranslate";
import { SOCIAL_ICONS } from "@/components/SocialIcons";

interface FooterLink { label: string; href: string; }
interface FooterColumn { title: string; links: FooterLink[]; }
interface SocialLink { name: string; href: string; icon: string; }
interface FooterSettings {
  description: string | null;
  copyright: string | null;
  columns: FooterColumn[];
  socials: SocialLink[];
}

const ICONS = SOCIAL_ICONS;

const Footer = () => {
  const { t } = useTranslation();
  const L = useL();
  const [data, setData] = useState<FooterSettings | null>(null);

  useEffect(() => {
    supabase
      .from("footer_settings")
      .select("description, copyright, columns, socials")
      .eq("id", 1)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setData(data as unknown as FooterSettings);
      });
  }, []);

  const [tDesc, tCopy] = useAITranslate([data?.description ?? "", data?.copyright ?? ""], "en");
  const columns = data?.columns ?? [];
  const socials = data?.socials ?? [];

  return (
    <footer className="py-16 bg-primary text-primary-foreground">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <a
              href="https://maroctransfers.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block font-heading text-2xl font-bold text-accent hover:text-accent/80 transition-colors mb-4"
            >
              Maroctransfers.com
            </a>
            <p className="font-body text-sm opacity-90 leading-relaxed">
              {tDesc || t("footer.description")}
            </p>
            {socials.length > 0 && (
              <div className="flex items-center gap-3 mt-5">
                {socials.map((s, i) => {
                  const Icon = ICONS[s.icon?.toLowerCase()] ?? Globe;
                  return (
                    <a
                      key={i}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.name}
                      className="w-9 h-9 rounded-full bg-white/10 hover:bg-accent hover:text-accent-foreground flex items-center justify-center transition-colors"
                    >
                      <Icon className="w-4 h-4" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {columns.map((col, i) => (
            <div key={i}>
              <h4 className="font-heading text-lg font-semibold mb-4">{col.title}</h4>
              <ul className="space-y-2 font-body text-sm opacity-90">
                {col.links.map((l, j) => (
                  <li key={j}>
                    <a href={l.href} className="hover:text-accent transition-colors">{l.label}</a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 pt-6 border-t border-primary-foreground/20 flex flex-col items-center gap-4 text-center">
          <nav aria-label="Useful links" className="flex flex-wrap items-center justify-center gap-4 font-body text-sm">
            <a href="/" className="hover:text-accent transition-colors">{L('Home', 'Accueil')}</a>
            <a href="/tarifs" className="hover:text-accent transition-colors">{L('Prices', 'Tarifs')}</a>
            <a href="/guide" className="hover:text-accent transition-colors">Guide</a>
            <a href="/surfing" className="hover:text-accent transition-colors">Surf</a>
          </nav>
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
            <span className="font-body text-xs uppercase tracking-[0.2em] opacity-70">
              {L('Related sites', 'Sites associés')}
            </span>
            <div className="flex items-center gap-4">
              <a
                href="https://varchecker.uk"
                target="_blank"
                rel="noopener noreferrer"
                className="font-heading text-sm font-semibold text-primary-foreground/90 hover:text-accent transition-colors"
              >
                Varchecker.uk
              </a>
              <span className="opacity-40">•</span>
              <a
                href="https://joxiq.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-heading text-sm font-semibold text-primary-foreground/90 hover:text-accent transition-colors"
              >
                Joxiq.com
              </a>
            </div>
          </div>
          <div className="w-full flex flex-col md:flex-row md:justify-between items-center gap-2 md:text-left">
          <p className="font-body text-xs opacity-90">
            {tCopy || `© 2008 maroctransfers.com. ${t("footer.rights")}`}
          </p>
          <p className="font-body text-xs opacity-90 md:text-right">
            {t("footer.designedBy")}{" "}
            <a
              href="https://varchecker.uk/"
              target="_blank"
              rel="dofollow noopener"
              className="text-accent hover:underline font-semibold"
            >
              Varchecker
            </a>
          </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
